/**
 * getApis — Centralised data-fetching helpers for all list endpoints.
 *
 * listRequestBody: DRY wrapper that POSTs to any backend list endpoint,
 * sets the state via svRef on success, or logs + shows an alert on failure.
 * Includes retry logic with exponential backoff for transient failures.
 *
 * Each named export is a thin binding of listRequestBody to a specific route.
 */
import axios from "axios";
import MuiAlert from "./muiAlerts"
import { backendOrigin } from "./backendOrigin";

const PAGED_REQUEST_TIMEOUT_MS = 45000;

/**
 * In-flight request deduplication map.
 * Key: "url||sortedPayloadJSON", Value: the in-flight Promise.
 * When the same logical request is fired again before the first resolves,
 * the existing promise is returned instead of launching a duplicate call.
 * Automatically removed from the map when the request settles.
 */
const _inflightRequests = new Map();

const _dedupKey = (url, payload) => {
  try {
    // Sort keys so { a:1, b:2 } and { b:2, a:1 } produce the same key
    const sorted = Object.keys(payload || {}).sort().reduce((acc, k) => {
      acc[k] = payload[k];
      return acc;
    }, {});
    return `${url}||${JSON.stringify(sorted)}`;
  } catch {
    return url;
  }
};

/**
 * Wraps an async factory function with in-flight deduplication.
 * If an identical request (same URL + payload) is already pending,
 * returns the existing promise rather than creating another one.
 */
const withDedupe = (url, payload, factory) => {
  const key = _dedupKey(url, payload);
  if (_inflightRequests.has(key)) {
    return _inflightRequests.get(key);
  }
  const promise = factory().finally(() => _inflightRequests.delete(key));
  _inflightRequests.set(key, promise);
  return promise;
};

const HEAVY_LIST_ENDPOINTS = new Set([
  'get-service-list',
  'get-user-list',
  'get-product-list-paged',
  'get-invoice-list-paged',
  'get-audiometry-list-paged',
  'get-patient-list-paged',
  'get-service-list-paged',
]);

const getRetryPolicy = (url) => {
  if (HEAVY_LIST_ENDPOINTS.has(url)) {
    // Heavy list queries should not be replayed aggressively.
    return { maxRetries: 0, baseDelay: 0 };
  }

  return { maxRetries: 1, baseDelay: 500 };
};

/**
 * Retry logic with exponential backoff.
 * Retries on network errors and 503 Quota Exceeded errors.
 * @param {Function} fn - Async function to retry
 * @param {number} maxRetries - Maximum number of retry attempts (default: 2)
 * @param {number} baseDelay - Initial delay in ms (default: 500)
 */
const retryWithBackoff = async (fn, maxRetries = 2, baseDelay = 500) => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      const isNetworkError = !error.response;
      const isQuotaError = error.response?.status === 503;
      const isTransient = isNetworkError || isQuotaError;

      if (attempt < maxRetries && isTransient) {
        // Exponential backoff: 500ms, 1000ms, 2000ms...
        const delay = baseDelay * Math.pow(2, attempt);
        console.warn(`[Retry ${attempt + 1}/${maxRetries}] Retrying after ${delay}ms...`, error.message);
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        throw error;
      }
    }
  }
};

/**
 * Generic POST helper for list-fetching endpoints with retry logic.
 * Caches successful responses with timestamps for offline/freshness tracking.
 * @param {string}   url             - Backend route path (without leading slash)
 * @param {object}   currentUserInfo - Firebase user object ({ uid, displayName })
 * @param {Function} svRef           - State setter to call with the response data array
 */
const listRequestBody = async (url, currentUserInfo, svRef) => {
  if (!currentUserInfo?.uid) {
    svRef([]);
    return;
  }

  try {
    const data = await fetchListData(url, currentUserInfo);
    svRef(data ?? []);
  } catch (err) {
    svRef([]);
    console.error(`[Error] ${url}:`, err);
    if (!err.response && err.message === 'Network Error') {
      MuiAlert.fire('Network Error', 'No internet connection. Check your network.', 'error');
    } else if (err.response?.status === 503) {
      MuiAlert.fire('Service Temporarily Unavailable',
        err.response?.data?.message || 'Database quota exceeded. Try again in a moment.', 'error');
    } else if (err.response?.status === 401) {
      MuiAlert.fire('Unauthorized', 'Your session expired. Please sign in again.', 'error');
    } else {
      MuiAlert.fire('Error', err.response?.data?.message || err.message || 'Failed to fetch data', 'error');
    }
  }
};

const fetchListData = async (url, currentUserInfo) => {
  if (!currentUserInfo?.uid) {
    return [];
  }

  const payload = { current_user_uid: currentUserInfo.uid, current_user_name: currentUserInfo.displayName };
  const factory = async () => {
    const policy = getRetryPolicy(url);
    return retryWithBackoff(async () => {
      const res = await axios.post(
        `${backendOrigin}/${url}`,
        payload,
        { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
      );
      if (res.data.operation === 'success') {
        return res.data.info || [];
      }
      throw new Error(res.data.message || `Failed to fetch /${url}`);
    }, policy.maxRetries, policy.baseDelay);
  };

  return withDedupe(url, payload, factory);
}

const getBranchList = (currentUserInfo, svRef) => listRequestBody("get-branch-list", currentUserInfo, svRef)
const getSalespersonList = (currentUserInfo, svRef) => listRequestBody("get-salesperson-list", currentUserInfo, svRef)
const getDoctorList = (currentUserInfo, svRef) => listRequestBody("get-doctor-list", currentUserInfo, svRef)
const getUserList = (currentUserInfo, svRef) => listRequestBody("get-user-list", currentUserInfo, svRef)
const getServiceList = (currentUserInfo, svRef) => listRequestBody("get-service-list", currentUserInfo, svRef)
const fetchBranchList = (currentUserInfo) => fetchListData('get-branch-list', currentUserInfo)
const fetchSalespersonList = (currentUserInfo) => fetchListData('get-salesperson-list', currentUserInfo)
const fetchDoctorList = (currentUserInfo) => fetchListData('get-doctor-list', currentUserInfo)
const fetchUserList = (currentUserInfo) => fetchListData('get-user-list', currentUserInfo)
const fetchServiceList = (currentUserInfo) => fetchListData('get-service-list', currentUserInfo)

/**
 * Generic POST helper for cursor-paginated list endpoints.
 * Returns { items, nextCursor, hasMore } for progressive loading.
 * @param {string} url              - Backend route path (without leading slash)
 * @param {object} currentUserInfo  - Firebase user object ({ uid, displayName })
 * @param {number} [limit=50]       - Page size
 * @param {string|null} [cursor]    - Firestore doc ID of last item from previous page
 * @returns {Promise<{ items: object[], nextCursor: string|null, hasMore: boolean }>}
 */
const pagedListRequest = async (url, currentUserInfo, limit = 50, cursor = null, extraPayload = {}) => {
  const payload = {
    current_user_uid: currentUserInfo.uid,
    current_user_name: currentUserInfo.displayName,
    limit,
    cursor,
    ...extraPayload,
  };
  const factory = async () => {
    const policy = getRetryPolicy(url);
    return retryWithBackoff(async () => {
      const res = await axios.post(
        `${backendOrigin}/${url}`,
        payload,
        { headers: { 'Content-Type': 'application/json' }, timeout: PAGED_REQUEST_TIMEOUT_MS }
      );
      if (res.data.operation === 'success') {
        return res.data.info; // { items, nextCursor, hasMore }
      }
      console.warn(`[Paged] API error: ${res.data.message}`);
      MuiAlert.fire('Warning', res.data.message || 'Failed to fetch data', 'warning');
      return { items: [], nextCursor: null, hasMore: false };
    }, policy.maxRetries, policy.baseDelay);
  };

  try {
    return await withDedupe(url, payload, factory);
  } catch (err) {
    console.error(`[PagedError] ${url}:`, err);
    if (!err.response && err.message === 'Network Error') {
      MuiAlert.fire('Network Error', 'No internet connection. Check your network.', 'error');
    } else if (err.response?.status === 503) {
      MuiAlert.fire('Service Temporarily Unavailable',
        err.response?.data?.message || 'Database quota exceeded. Try again in a moment.', 'error');
    } else if (err.response?.status === 401) {
      MuiAlert.fire('Unauthorized', 'Your session expired. Please sign in again.', 'error');
    } else {
      MuiAlert.fire('Error', err.response?.data?.message || err.message || 'Failed to fetch data', 'error');
    }
    return { items: [], nextCursor: null, hasMore: false };
  }
};

const getInvoiceListPaged    = (currentUserInfo, limit, cursor, branchId = null) => pagedListRequest('get-invoice-list-paged', currentUserInfo, limit, cursor, branchId ? { branch_id: branchId } : {})

/**
 * Fetches ALL invoices for a given month and branch for the Monthly Report tab.
 * Uses a dedicated date-range endpoint — not limited by paginated Records data.
 * @param {object} currentUserInfo - Firebase user object
 * @param {string} branchId       - Branch document ID
 * @param {string} yearMonth      - "YYYY-MM" string
 * @returns {Promise<object[]>}    Full invoice list for the month
 */
const getInvoicesForMonthReport = async (currentUserInfo, branchId, yearMonth) => {
  if (!currentUserInfo || !branchId || !yearMonth) return []
  try {
    const res = await axios.post(
      `${backendOrigin}/get-invoices-for-month-report`,
      {
        branch_id: branchId,
        year_month: yearMonth,
        current_user_uid: currentUserInfo.uid,
        current_user_name: currentUserInfo.displayName,
      },
      { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
    )
    if (res.data.operation === 'success') {
      return Array.isArray(res.data.info) ? res.data.info : []
    }
    return []
  } catch (err) {
    console.warn('[getInvoicesForMonthReport] failed:', err?.message || err)
    return []
  }
}

const getDashboardSummary = async (currentUserInfo) => {
  if (!currentUserInfo?.uid) return null

  const res = await axios.post(
    `${backendOrigin}/reports/dashboard-summary`,
    {
      current_user_uid: currentUserInfo.uid,
      current_user_name: currentUserInfo.displayName,
    },
    { headers: { 'Content-Type': 'application/json' }, timeout: 15000 }
  )

  if (res.data.operation === 'success') {
    return res.data.info || null
  }

  throw new Error(res.data.message || 'Failed to fetch dashboard summary')
}
const getProductListPaged = (currentUserInfo, limit, cursor, branchIdOrOptions = null) => {
  let branchId = null
  let inStockOnly = false

  if (branchIdOrOptions && typeof branchIdOrOptions === 'object' && !Array.isArray(branchIdOrOptions)) {
    branchId = branchIdOrOptions.branchId || null
    inStockOnly = branchIdOrOptions.inStockOnly === true
  } else {
    branchId = branchIdOrOptions
  }

  const extraPayload = {}
  if (branchId) extraPayload.branch_id = branchId
  if (inStockOnly) extraPayload.in_stock_only = true

  return pagedListRequest('get-product-list-paged', currentUserInfo, limit, cursor, extraPayload)
}
const getPatientListPaged    = (currentUserInfo, limit, cursor) => pagedListRequest('get-patient-list-paged', currentUserInfo, limit, cursor)
const getAudiometryListPaged = (currentUserInfo, limit, cursor, branchId = null) => pagedListRequest('get-audiometry-list-paged', currentUserInfo, limit, cursor, branchId ? { branch_id: branchId } : {})
const getServiceListPaged    = (currentUserInfo, limit, cursor) => pagedListRequest('get-service-list-paged', currentUserInfo, limit, cursor)

const getPatientsBriefByIds = async (currentUserInfo, patientIds = []) => {
  if (!currentUserInfo || !Array.isArray(patientIds) || patientIds.length === 0) {
    return []
  }

  const res = await axios.post(
    `${backendOrigin}/get-patients-brief-by-ids`,
    {
      patient_ids: patientIds,
      current_user_uid: currentUserInfo.uid,
      current_user_name: currentUserInfo.displayName,
    },
    { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
  )

  if (res.data.operation === 'success') {
    return Array.isArray(res.data.info) ? res.data.info : []
  }

  return []
}

const searchPatientsBrief = async (currentUserInfo, searchTerm, limit = 25) => {
  if (!currentUserInfo || !(searchTerm || '').trim()) {
    return []
  }

  const res = await axios.post(
    `${backendOrigin}/search-patients-brief`,
    {
      search_term: searchTerm,
      limit,
      current_user_uid: currentUserInfo.uid,
      current_user_name: currentUserInfo.displayName,
    },
    { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
  )

  if (res.data.operation === 'success') {
    return Array.isArray(res.data.info) ? res.data.info : []
  }

  return []
}

const searchProductBrief = async (currentUserInfo, searchTerm, { limit = 25, branchId = null, inStockOnly = true } = {}) => {
  if (!currentUserInfo || !(searchTerm || '').trim()) {
    return []
  }

  const res = await axios.post(
    `${backendOrigin}/search-product-brief`,
    {
      search_term: searchTerm,
      limit,
      branch_id: branchId,
      in_stock_only: inStockOnly,
      current_user_uid: currentUserInfo.uid,
      current_user_name: currentUserInfo.displayName,
    },
    { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
  )

  if (res.data.operation === 'success') {
    return Array.isArray(res.data.info) ? res.data.info : []
  }

  return []
}

const getCachePolicySettings = async (currentUserInfo) => {
  if (!currentUserInfo?.uid) return null
  const res = await axios.get(
    `${backendOrigin}/get-cache-policy-settings`,
    { 
      headers: { 'Content-Type': 'application/json' }, 
      timeout: 20000 
    }
  )

  if (res.data.operation === 'success') {
    return res.data.info || null
  }

  throw new Error(res.data.message || 'Failed to fetch cache policy settings')
}

const saveCachePolicySettings = async (currentUserInfo, payload) => {
  if (!currentUserInfo?.uid) {
    throw new Error('Missing authenticated user context')
  }

  const res = await axios.post(
    `${backendOrigin}/save-cache-policy-settings`,
    {
      ...payload,
      current_user_uid: currentUserInfo.uid,
      current_user_name: currentUserInfo.displayName,
    },
    { headers: { 'Content-Type': 'application/json' }, timeout: 20000 }
  )

  if (res.data.operation === 'success') {
    return res.data.info || null
  }

  throw new Error(res.data.message || 'Failed to save cache policy settings')
}

export { 
  getBranchList, 
  getSalespersonList, 
  getDoctorList, 
  getUserList, 
  getServiceList,
  fetchBranchList,
  fetchSalespersonList,
  fetchDoctorList,
  fetchUserList,
  fetchServiceList,
  getInvoiceListPaged,
  getInvoicesForMonthReport,
  getDashboardSummary,
  getProductListPaged,
  getPatientListPaged,
  getAudiometryListPaged,
  getServiceListPaged,
  getPatientsBriefByIds,
  searchPatientsBrief,
  searchProductBrief,
  getCachePolicySettings,
  saveCachePolicySettings,
}